import React from 'react';
import { Link } from 'react-router-dom';

interface AdPartner {
  name: string;
  privacyPolicy: string;
  choice: string;
  choiceNote?: string;
}

const partners: AdPartner[] = [
  {
    name: 'AppLovin',
    privacyPolicy: 'https://legal.applovin.com/privacy/',
    choice: 'https://dash.applovin.com/p/how-applovin-shows-you-ads',
  },
  {
    name: 'AppsFlyer',
    privacyPolicy: 'https://www.appsflyer.com/legal/services-privacy-policy/',
    choice: 'https://www.appsflyer.com/optout',
  },
  {
    name: 'Google Ad Products (inc. AdMob)',
    privacyPolicy: 'https://policies.google.com/privacy',
    choice: 'https://policies.google.com/technologies/product-privacy',
  },
  {
    name: 'IronSource',
    privacyPolicy: 'https://unity.com/legal/privacy-policy',
    choice: 'https://unity.com/legal/developer-privacy-policy',
    choiceNote: 'Section 7 – "Your Choices About Unity\'s Collection and Use of Your Information"',
  },
  {
    name: 'Liftoff',
    privacyPolicy: 'https://liftoff.ai/privacy-policy/',
    choice: 'https://liftoff.ai/opt-out/',
  },
  {
    name: 'Meta Audience Network / Meta Ads',
    privacyPolicy: 'https://www.facebook.com/privacy/policy/',
    choice: 'https://www.facebook.com/privacy/policy/?section_id=6-HowCanYouManage',
  },
  {
    name: 'TikTok Ads',
    privacyPolicy: 'https://www.tiktok.com/legal/privacy-policy-us?lang=en',
    choice: 'https://www.tiktok.com/legal/cookie-policy?lang=en',
    choiceNote: 'Section 4',
  },
  {
    name: 'Unity Ads',
    privacyPolicy: 'https://unity.com/legal/privacy-policy',
    choice: 'https://unity.com/legal/developer-privacy-policy',
    choiceNote: 'Section 7 – "Your Choices About Unity\'s Collection and Use of Your Information"',
  },
];

const ExternalLink: React.FC<{ href: string }> = ({ href }) => (
  <a href={href} target="_blank" rel="noopener noreferrer">{href}</a>
);

const AdPartners: React.FC = () => {
  return (
    <div className="legal-page">
      <div className="legal-nav">
        <Link to="/" className="back-link">&larr; Back to Home</Link>
      </div>

      <h1>Personalized Advertising Consent</h1>
      <p className="effective-date">Last Updated: September 2026</p>

      <p>
        Closing Theory Studios, Inc. ("Closing Theory," "we," "us," or "our") displays advertising in Househunt to help us deliver a free-to-play game experience. Where permissible, Closing Theory and our advertising partners listed below will show you personalized advertising both on and off our sites and apps. Where legally required, we will provide you with choices about personalized advertising. If you do not agree to personalized advertising, then you will still see advertising, but this advertising will not be personalized and will be less relevant to you.
      </p>
      <p>
        Please note that some of our advertising partners (e.g., Meta) collect your consent to personalized advertising through other third-party sites or apps, which means that if you have already consented to these partners serving you personalized advertising, then they may continue to do so in Househunt even if you decline to consent in Househunt. To stop receiving personalized advertising from these partners, please contact them directly using the links below.
      </p>
      <p>
        Our advertising partners may also share information with their own demand partners and advertising exchanges in order to serve ads. Please see each partner's privacy policy for details.
      </p>
      <p>
        For information about the information we collect and process, see our <Link to="/privacy-policy">Privacy Policy</Link>.
      </p>
      <p>
        You can change your mind about your choices at any time. On iOS, you can control whether apps may request to track you across other companies' apps and websites in Settings &gt; Privacy &amp; Security &gt; Tracking. You may also contact us at <a href="mailto:legal@closingtheory.com">legal@closingtheory.com</a> with any questions or requests regarding personalized advertising.
      </p>

      <h2>Househunt Advertising Partners</h2>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Advertising Partner</th>
              <th>Privacy Policy</th>
              <th>User's Choice</th>
            </tr>
          </thead>
          <tbody>
            {partners.map((partner) => (
              <tr key={partner.name}>
                <td>{partner.name}</td>
                <td><ExternalLink href={partner.privacyPolicy} /></td>
                <td>
                  <ExternalLink href={partner.choice} />
                  {partner.choiceNote && <> ({partner.choiceNote})</>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdPartners;
